import { createApi, fetchBaseQuery, BaseQueryFn } from '@reduxjs/toolkit/query/react';
import { getApiConfig } from '@/config/api';
import { getRequestLanguage } from '@/utils/requestLanguage';
import type { Category, ApiResponse } from '@/types';

// Create a dynamic base query for category API
const dynamicCategoryBaseQuery: BaseQueryFn = async (args, api, extraOptions) => {
  const config = getApiConfig();

  const baseQuery = fetchBaseQuery({
    baseUrl: config.API_URL,
    prepareHeaders: (headers, { getState }) => {
      let token = (getState() as any).auth.token;
      if (!token) {
        token = localStorage.getItem('token');
      }
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      headers.set('accept-language', getRequestLanguage());
      return headers;
    },
  });

  return baseQuery(args, api, extraOptions);
};

export const categoryApi = createApi({
  reducerPath: 'categoryApi',
  baseQuery: dynamicCategoryBaseQuery,
  tagTypes: ['Category'],
  endpoints: (builder) => ({
    // Get all categories
    getCategories: builder.query<ApiResponse<Category[]>, { storeId?: string; includeInactive?: boolean; includeDeleted?: boolean } | void>({
      query: (params) => ({
        url: '/categories',
        params: params || undefined,
      }),
      providesTags: ['Category'],
    }),

    // Get single category
    getCategory: builder.query<ApiResponse<Category>, string>({
      query: (id) => `/categories/${id}`,
      providesTags: (_, __, id) => [{ type: 'Category', id }],
    }),

    // Create category
    createCategory: builder.mutation<
      ApiResponse<Category>,
      { storeId: string; name: string; description?: string; isActive?: boolean }
    >({
      query: (data) => ({
        url: '/categories',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Category'],
    }),

    // Update category
    updateCategory: builder.mutation<
      ApiResponse<Category>,
      { id: string; data: { name?: string; description?: string; isActive?: boolean } }
    >({
      query: ({ id, data }) => ({
        url: `/categories/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_, __, { id }) => [{ type: 'Category', id }],
    }),

    // Delete category
    deleteCategory: builder.mutation<ApiResponse<void>, string>({
      query: (id) => ({
        url: `/categories/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Category'],
    }),
  }),
});

export const {
  useGetCategoriesQuery,
  useGetCategoryQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
} = categoryApi;
